import type { ReactNode } from 'react'

// 这里把套餐信息收口成纯数据，避免在常量文件里混入 hook、按钮和跳转逻辑。
// 首页 Pricing 与 /price 对比表都基于同一份结构扩展，后续再调样式时不会互相打架。
export type TierActionKind = 'sign-in' | 'buy-vip' | 'buy-premium' | 'contact'

export type TierFeature = {
  icon: boolean | ReactNode
  description: ReactNode
}

export type Tier = {
  id: 'free' | 'pro' | 'advanced' | 'enterprise'
  name: string
  priceMonthly: string
  priceUnit?: string
  subtitle?: string
  description: string
  features: TierFeature[]
  variant: 'default' | 'featured' | 'enterprise'
  action: {
    label: string
    kind: TierActionKind
  }
}

const AiIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.34863 3.23723C5.27361 2.9704 4.89537 2.9704 4.82036 3.23723L4.61007 3.98524C4.30586 5.06734 3.43597 5.89755 2.34087 6.15094C2.15808 6.19323 2.13777 6.44543 2.31144 6.51642L2.41876 6.56029C3.48763 6.99723 4.2883 7.91206 4.57976 9.0294L4.77077 9.76167C4.84502 10.0463 5.25421 10.0309 5.30683 9.74147L5.37724 9.35422C5.60928 8.07804 6.49287 7.01668 7.70583 6.55713L7.83215 6.50928C8.00794 6.44268 7.9908 6.18858 7.80765 6.1462C6.72516 5.89573 5.8653 5.07509 5.5646 4.00547L5.34863 3.23723ZM11.0476 5.22511C11.2962 4.32961 12.5586 4.30946 12.8357 5.19656L13.0029 5.73222C13.61 7.67594 15.0317 9.25996 16.8987 10.0728C17.6061 10.3807 17.5934 11.3889 16.8821 11.6877C14.8963 12.5219 13.3882 14.2195 12.8027 16.2923L12.3462 17.9085C12.2114 18.3858 11.5387 18.3967 11.3885 17.924L10.8256 16.1524C10.1924 14.1601 8.70809 12.5419 6.78069 11.7322C6.05007 11.4253 6.05414 10.3841 6.79056 10.0913C8.84845 9.27333 10.4051 7.53958 10.9974 5.40577L11.0476 5.22511ZM16.2364 14.2251C16.2975 14.0166 16.5929 14.0166 16.6539 14.2251L16.7106 14.4188C16.8462 14.882 17.2138 15.2408 17.6801 15.3653C17.8326 15.406 17.855 15.6132 17.7147 15.6856L17.5851 15.7525C17.1642 15.9698 16.8438 16.3416 16.6911 16.79L16.5966 17.0676C16.548 17.2103 16.3457 17.2092 16.2987 17.066L16.1888 16.7312C16.0436 16.289 15.7228 15.926 15.3019 15.7275L15.1968 15.6779C15.0541 15.6106 15.0729 15.402 15.2253 15.3613C15.6823 15.2393 16.0425 14.8876 16.1754 14.4337L16.2364 14.2251Z"
        fill="url(#paint0_linear_116270_46775)"
      />
      <defs>
        <linearGradient id="paint0_linear_116270_46775" x1="6.51756" y1="8.02077" x2="20.5347" y2="19.2964" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2825FF" />
          <stop offset="0.45" stopColor="#00C4FF" />
          <stop offset="0.795" stopColor="#00FF94" />
        </linearGradient>
      </defs>
    </svg>
  )
}

// 套餐顺序与 Figma 4 卡顺序保持一致：首页与价格页都按这份数组渲染。
export const tiers: Tier[] = [
  {
    id: 'free',
    name: '免费版',
    priceMonthly: '0',
    priceUnit: '元',
    description: '满足基础使用与体验需求',
    variant: 'default',
    action: {
      label: '免费体验',
      kind: 'sign-in'
    },
    features: [
      {
        description: '免费赠送 30 AI贝',
        icon: true
      },
      {
        description: '享站内所有 AI 功能权益',
        icon: true
      },
      {
        description: '海量专业图表',
        icon: true
      },
      {
        description: '实用小组件（限免）',
        icon: true
      },
      {
        description: '5 个项目存储',
        icon: true
      },
      {
        description: '500MB 存储空间',
        icon: true
      }
    ]
  },
  {
    id: 'pro',
    name: '标准版',
    priceMonthly: '8.6',
    priceUnit: '元/月起',
    description: '覆盖「免费版」所有权益/功能',
    variant: 'default',
    action: {
      label: '即刻体验',
      kind: 'buy-vip'
    },
    features: [
      {
        description: '230 AI贝/月',
        icon: <AiIcon />
      },
      {
        description: '会员专属图文模板',
        icon: true
      },
      {
        description: 'SVG 等全部导出格式',
        icon: true
      },
      {
        description: '自定义倍数导出',
        icon: true
      },
      {
        description: '个人商用授权',
        icon: true
      },
      {
        description: '去水印',
        icon: true
      },
      {
        description: '100 个项目存储',
        icon: true
      },
      {
        description: '50GB 存储空间',
        icon: true
      },
      {
        description: '回收站 30 天还原',
        icon: true
      }
    ]
  },
  {
    id: 'advanced',
    name: '高级版',
    priceMonthly: '14.9',
    priceUnit: '元/月起',
    description: '覆盖「标准版」所有权益/功能',
    variant: 'featured',
    action: {
      label: '即刻体验',
      kind: 'buy-premium'
    },
    features: [
      {
        description: '1200 AI贝/月',
        icon: <AiIcon />
      },
      {
        description: 'AI 趋势分析无限使用',
        icon: true
      },
      {
        description: '200 个项目存储',
        icon: true
      },
      {
        description: '100GB 存储空间',
        icon: true
      }
    ]
  },
  {
    id: 'enterprise',
    name: '企业版',
    priceMonthly: '请咨询顾问',
    subtitle: '适合政府、媒体、国企及各类组织客户',
    description: '按需定制，专属方案',
    variant: 'enterprise',
    action: {
      label: '联系顾问',
      kind: 'contact'
    },
    features: [
      {
        description: '按token 用量计费',
        icon: true
      },
      {
        description: '支持更高并发与更大调用量',
        icon: true
      },
      {
        description: '支持 API 接口接入',
        icon: true
      },
      {
        description: '支持本地化 / 私有化部署',
        icon: true
      },
      {
        description: '支持团队权限管理',
        icon: true
      },
      {
        description: '支持专属模板与品牌定制',
        icon: true
      },
      {
        description: '支持接口对接与定制开发',
        icon: true
      },
      {
        description: '支持培训实施与项目交付',
        icon: true
      },
      {
        description: '支持对公采购与合同服务',
        icon: true
      }
    ]
  }
]
